/**
 * MONSTER TOWN — FURNITURE ART. OWNED BY THE INTERIORS CREW.
 *
 * Every builder here obeys the `ArtBuilder` contract:
 *   - origin is the FOOTPRINT CENTRE at floor level (the caller positions and
 *     rotates it),
 *   - deterministic for the same ArtContext,
 *   - only cached kit geometry/materials — never `new THREE.Material()`,
 *   - front faces +Z (south) at rot 0,
 *   - everything sized off `ctx.grid.tileSize`, so the same piece works at
 *     interior scale (1u) and exterior scale (2u).
 *
 * PARTS ARE NAMED ON PURPOSE. `prop:anim` / `prop:toggle` find them by name, so
 * `lid`, `door`, `bulb`/`bulbOff`, `water`/`waterOff`, `screen`/`screenOff`,
 * `books`, `hands`, `fish`, `blanket`, `seat` are a contract with
 * `src/content/furniture.ts`. On/off parts read the saved instance state here so
 * a lamp a kid left on is still on tomorrow.
 */

import * as THREE from 'three'
import type { ArtContext } from '../core/placeable'
import type { PaletteName } from '../core/palette'
import { ball, box, boxOn, cone, cylinder, generatedTexture, group, inked, planeGeo, toon } from '../render/kit'

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

/**
 * CONTACT SHADOW. One shared material, one shared texture, one quad per piece.
 *
 * The sun is a single directional light and the ambient is even, so a chair and
 * the floor under it were lit identically and every object in every room looked
 * like it was hovering a centimetre above the ground. Real shadow mapping does
 * exist here, but at this sun angle and this render scale the contact point —
 * the bit that actually tells your eye an object is TOUCHING — is the first
 * thing that gets blurred away, and turning the shadow map up is the wrong
 * thing to spend an older iPad's budget on.
 *
 * A baked blob under each piece costs one transparent quad and does the job at
 * any quality tier, including 'low' where shadows are switched off entirely.
 */
let blobMat: THREE.Material | null = null
function blobMaterial(): THREE.Material {
  if (blobMat === null) {
    blobMat = new THREE.MeshBasicMaterial({
      map: generatedTexture('contactBlob2', (c, s) => {
        /*
         * ROUND-2 RETUNE. The old blob was the same size as the piece standing
         * on it, so for anything with a solid base — the fridge, the stove, the
         * sink, the couch, the bookshelf — it was entirely hidden UNDER the
         * object and only pieces with legs (the table) looked grounded at all.
         * The critic read that, correctly, as "contact shadows are inconsistent".
         * The blob is now drawn well inside its quad and every caller's quad is
         * scaled up (see `grounded`), so a soft skirt of shade always shows
         * around the base no matter which way the camera is turned.
         */
        const g = c.createRadialGradient(s / 2, s / 2, s * 0.16, s / 2, s / 2, s * 0.5)
        g.addColorStop(0, 'rgba(43,36,56,0.50)')
        g.addColorStop(0.5, 'rgba(43,36,56,0.34)')
        g.addColorStop(0.72, 'rgba(43,36,56,0.15)')
        g.addColorStop(0.88, 'rgba(43,36,56,0.04)')
        g.addColorStop(1, 'rgba(43,36,56,0)')
        c.clearRect(0, 0, s, s)
        c.fillStyle = g
        c.fillRect(0, 0, s, s)
      }),
      transparent: true,
      depthWrite: false,
    })
  }
  return blobMat
}

/**
 * Drop a contact shadow under a piece. `w`/`d` are the shadow's size in world
 * units — a little wider than the object that casts it. Skipped for ghosts:
 * a build preview is already translucent and a shadow under it reads as placed.
 */
export function grounded(g: THREE.Group, ctx: ArtContext, w: number, d = w): THREE.Group {
  if (ctx.ghost) return g
  /*
   * The quad is 1.34x what the caller asked for. Callers pass "a little wider
   * than the object", which put the whole gradient underneath the object; the
   * extra third is the visible skirt. Rounded to 2dp so `planeGeo`'s cache still
   * gets hits across pieces of the same size.
   */
  const s = 1.34
  const qw = Math.round(w * s * 100) / 100
  const qd = Math.round(d * s * 100) / 100
  const m = new THREE.Mesh(planeGeo(qw, qd), blobMaterial())
  m.position.y = 0.016
  m.renderOrder = -1
  m.name = 'contactShadow'
  g.add(m)
  return g
}

/** The warm pool a lit lamp throws on the floor. Part of the `bulb` group. */
function lightPool(t: number, r: number): THREE.Mesh {
  const m = new THREE.Mesh(planeGeo(r * 2, r * 2), lightPoolMaterial())
  m.position.y = 0.02 * t
  m.renderOrder = -1
  m.name = 'lightPool'
  return m
}

let haloMat: THREE.Material | null = null
function haloMaterial(): THREE.Material {
  if (haloMat === null) {
    haloMat = new THREE.MeshBasicMaterial({
      color: 0xfff3d6,
      transparent: true,
      opacity: 0.28,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    })
  }
  return haloMat
}

let poolMat: THREE.Material | null = null
function lightPoolMaterial(): THREE.Material {
  if (poolMat === null) {
    poolMat = new THREE.MeshBasicMaterial({
      map: generatedTexture('lampPool', (c, s) => {
        const g = c.createRadialGradient(s / 2, s / 2, s * 0.03, s / 2, s / 2, s * 0.5)
        g.addColorStop(0, 'rgba(255,243,214,0.58)')
        g.addColorStop(0.45, 'rgba(255,214,140,0.30)')
        g.addColorStop(0.8, 'rgba(255,214,140,0.08)')
        g.addColorStop(1, 'rgba(255,214,140,0)')
        c.clearRect(0, 0, s, s)
        c.fillStyle = g
        c.fillRect(0, 0, s, s)
      }),
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    })
  }
  return poolMat
}

/** Saved per-instance flag, for art that must remember on/off across a reload. */
export function flag(ctx: ArtContext, key: string, fallback: boolean): boolean {
  const v = ctx.instance?.state?.[key]
  return typeof v === 'boolean' ? v : fallback
}

/**
 * Saved per-instance NUMBER, for art that remembers more than on/off — which
 * channel the telly is on, which colour a thing was left. Same defensive shape
 * as `flag`: a save that has been hand-edited or has drifted a version gets the
 * fallback, never a NaN that quietly poisons a transform.
 */
export function count(ctx: ArtContext, key: string, fallback: number): number {
  const v = ctx.instance?.state?.[key]
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback
}

export function tint(ctx: ArtContext, fallback: PaletteName): PaletteName {
  return ctx.tint ?? fallback
}

/** Four chunky legs under a top surface. */
function legs(g: THREE.Group, w: number, d: number, h: number, color: PaletteName, r = 0.07): void {
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      g.add(
        boxOn(r * 2, h, r * 2, {
          color,
          x: (sx * (w / 2 - r * 1.6)),
          z: (sz * (d / 2 - r * 1.6)),
        }),
      )
    }
  }
}

/** A soft slab with a lighter top face — the vocabulary for anything cushioned. */
function cushion(
  w: number,
  h: number,
  d: number,
  color: PaletteName,
  o: { x?: number; y?: number; z?: number; name?: string },
): THREE.Group {
  const g = group(o.name ?? 'cushion')
  g.position.set(o.x ?? 0, o.y ?? 0, o.z ?? 0)
  g.add(boxOn(w, h, d, { color }))
  g.add(boxOn(w * 0.86, h * 0.16, d * 0.86, { color, y: h }))
  return g
}

// ---------------------------------------------------------------------------
// seating & tables
// ---------------------------------------------------------------------------

export function artCouch(ctx: ArtContext): THREE.Object3D {
  const t = ctx.grid.tileSize
  const g = group('couch')
  const c = tint(ctx, 'fabric')
  const base = boxOn(t * 1.85, t * 0.34, t * 0.8, { color: c, name: 'base' })
  g.add(inked(base))
  const seat = cushion(t * 1.7, t * 0.22, t * 0.66, 'cushion', { y: t * 0.34, name: 'seat' })
  g.add(seat)
  g.add(boxOn(t * 1.85, t * 0.66, t * 0.2, { color: c, y: t * 0.3, z: -t * 0.32, name: 'back' }))
  for (const s of [-1, 1]) {
    g.add(boxOn(t * 0.18, t * 0.5, t * 0.8, { color: c, x: s * t * 0.85, y: t * 0.3 }))
  }
  legs(g, t * 1.7, t * 0.7, t * 0.14, 'woodDark', t * 0.06)
  grounded(g, ctx, t * 1.98, t * 0.96)
  return g
}

export function artChair(ctx: ArtContext): THREE.Object3D {
  const t = ctx.grid.tileSize
  const g = group('chair')
  const c = tint(ctx, 'woodLight')
  const seat = boxOn(t * 0.62, t * 0.12, t * 0.62, { color: c, y: t * 0.42, name: 'seat' })
  g.add(inked(seat))
  g.add(boxOn(t * 0.62, t * 0.62, t * 0.1, { color: c, y: t * 0.54, z: -t * 0.26, name: 'back' }))
  legs(g, t * 0.58, t * 0.58, t * 0.42, 'woodDark', t * 0.05)
  grounded(g, ctx, t * 0.85, t * 0.85)
  return g
}

export function artTable(ctx: ArtContext): THREE.Object3D {
  const t = ctx.grid.tileSize
  const g = group('table')
  const c = tint(ctx, 'wood')
  const top = boxOn(t * 1.5, t * 0.14, t * 1.5, { color: c, y: t * 0.56, name: 'top' })
  g.add(inked(top))
  // Legs, not a pedestal. At a 34-degree iso a single centre stem hides behind
  // the top and the table reads as a slab floating over its own shadow.
  g.add(boxOn(t * 1.34, t * 0.1, t * 1.34, { color: 'woodDark', y: t * 0.46 }))
  legs(g, t * 1.36, t * 1.36, t * 0.46, 'woodDark', t * 0.07)

  // Laid for tea, hidden until a kid taps it. `dishes` is toggled by interact.
  const dishes = group('dishes')
  dishes.visible = flag(ctx, 'set', false)
  dishes.position.y = t * 0.7
  for (const [dx, dz] of [
    [-0.38, 0.28],
    [0.38, 0.28],
    [0, -0.36],
  ] as const) {
    dishes.add(cylinder(t * 0.2, t * 0.04, { color: 'plate', x: dx * t, z: dz * t }))
    dishes.add(cylinder(t * 0.09, t * 0.12, { color: 'iceStrawberry', x: dx * t, y: t * 0.04, z: dz * t }))
  }
  g.add(dishes)
  grounded(g, ctx, t * 1.8, t * 1.8)
  return g
}

export function artBed(ctx: ArtContext): THREE.Object3D {
  const t = ctx.grid.tileSize
  const g = group('bed')
  const frame = boxOn(t * 1.5, t * 0.3, t * 1.9, { color: 'wood', name: 'frame' })
  g.add(inked(frame))
  g.add(boxOn(t * 1.5, t * 0.7, t * 0.16, { color: 'wood', z: -t * 0.87, name: 'headboard' }))
  const blanket = group('blanket')
  blanket.position.y = t * 0.3
  blanket.add(boxOn(t * 1.42, t * 0.2, t * 1.3, { color: tint(ctx, 'fabricAlt'), z: t * 0.24 }))
  blanket.add(boxOn(t * 1.42, t * 0.06, t * 0.28, { color: 'wallCream', z: -t * 0.36 }))
  g.add(blanket)
  const pillow = cushion(t * 1.1, t * 0.18, t * 0.4, 'wallCream', {
    y: t * 0.3,
    z: -t * 0.6,
    name: 'pillow',
  })
  g.add(pillow)
  legs(g, t * 1.4, t * 1.8, t * 0.16, 'woodDark', t * 0.06)
  grounded(g, ctx, t * 1.9, t * 1.96)
  return g
}

export function artBookshelf(ctx: ArtContext): THREE.Object3D {
  const t = ctx.grid.tileSize
  const g = group('bookshelf')
  const c = tint(ctx, 'wood')
  const body = boxOn(t * 0.9, t * 1.5, t * 0.34, { color: c, name: 'body' })
  g.add(inked(body))
  const books = group('books')
  const colours: PaletteName[] = ['flowerRed', 'wallBlue', 'monsterLime', 'flowerPurple', 'uiCoin']
  for (let shelf = 0; shelf < 3; shelf++) {
    g.add(boxOn(t * 0.84, t * 0.05, t * 0.3, { color: 'woodDark', y: t * (0.4 + shelf * 0.4) }))
    for (let i = 0; i < 5; i++) {
      const col = colours[(shelf * 5 + i) % colours.length] ?? 'flowerRed'
      books.add(
        boxOn(t * 0.1, t * 0.26 + (i % 3) * t * 0.03, t * 0.22, {
          color: col,
          x: t * (-0.32 + i * 0.16),
          y: t * (0.45 + shelf * 0.4),
          z: t * 0.02,
        }),
      )
    }
  }
  g.add(books)
  grounded(g, ctx, t * 0.98, t * 0.6)
  return g
}

// ---------------------------------------------------------------------------
// kitchen & bath
// ---------------------------------------------------------------------------

export function artFridge(ctx: ArtContext): THREE.Object3D {
  const t = ctx.grid.tileSize
  const g = group('fridge')
  const c = tint(ctx, 'uiPanel')
  const body = boxOn(t * 0.86, t * 1.65, t * 0.8, { color: c, name: 'body' })
  g.add(inked(body))

  // What is inside, revealed while the door swings.
  const inner = group('inner')
  inner.add(boxOn(t * 0.7, t * 1.4, t * 0.05, { color: 'glassDark', y: t * 0.1, z: t * 0.36 }))
  for (let i = 0; i < 3; i++) {
    inner.add(
      boxOn(t * 0.16, t * 0.2, t * 0.16, {
        color: (['iceStrawberry', 'monsterLime', 'uiCoin'] as const)[i] ?? 'uiCoin',
        x: t * (-0.2 + i * 0.2),
        y: t * (0.4 + (i % 2) * 0.45),
        z: t * 0.3,
      }),
    )
  }
  g.add(inner)

  // The door pivots on its own group so `swing` hinges instead of sliding.
  const hinge = group('door')
  hinge.position.set(-t * 0.43, 0, t * 0.4)
  const leaf = boxOn(t * 0.86, t * 1.6, t * 0.1, { color: c, x: t * 0.43, y: t * 0.03 })
  hinge.add(leaf)
  hinge.add(boxOn(t * 0.06, t * 0.4, t * 0.08, { color: 'metalDark', x: t * 0.78, y: t * 0.9, z: t * 0.06 }))
  hinge.add(boxOn(t * 0.8, t * 0.05, t * 0.11, { color: 'metal', x: t * 0.43, y: t * 1.05 }))
  g.add(hinge)
  grounded(g, ctx, t * 0.98, t * 0.96)
  return g
}

export function artStove(ctx: ArtContext): THREE.Object3D {
  const t = ctx.grid.tileSize
  const g = group('stove')
  const body = boxOn(t * 0.9, t * 0.85, t * 0.8, { color: tint(ctx, 'metal'), name: 'body' })
  g.add(inked(body))
  g.add(boxOn(t * 0.86, t * 0.06, t * 0.76, { color: 'metalDark', y: t * 0.85 }))
  for (const [dx, dz] of [
    [-0.2, -0.18],
    [0.2, -0.18],
    [-0.2, 0.2],
    [0.2, 0.2],
  ] as const) {
    g.add(cylinder(t * 0.14, t * 0.03, { color: 'ink', x: dx * t, y: t * 0.91, z: dz * t, seg: 8 }))
  }
  // oven window + knobs
  g.add(boxOn(t * 0.62, t * 0.36, t * 0.05, { color: 'glassDark', y: t * 0.24, z: t * 0.4 }))
  for (let i = 0; i < 3; i++) {
    g.add(cylinder(t * 0.05, t * 0.05, { color: 'uiPrimary', x: t * (-0.25 + i * 0.25), y: t * 0.68, z: t * 0.41, seg: 8 }))
  }
  // the pot: `lid` hinges when a kid cooks
  const pot = group('pot')
  pot.position.set(-t * 0.2, t * 0.94, -t * 0.18)
  pot.add(cylinder(t * 0.22, t * 0.24, { color: 'metalDark' }))
  const lid = group('lid')
  lid.position.set(-t * 0.22, t * 0.24, 0)
  lid.add(cylinder(t * 0.23, t * 0.05, { color: 'metal', x: t * 0.22 }))
  lid.add(ball(t * 0.05, { color: 'uiPrimary', x: t * 0.22, y: t * 0.08 }))
  pot.add(lid)
  g.add(pot)
  grounded(g, ctx, t * 0.98, t * 0.96)
  return g
}

export function artSink(ctx: ArtContext): THREE.Object3D {
  const t = ctx.grid.tileSize
  const g = group('sink')
  const body = boxOn(t * 0.9, t * 0.68, t * 0.7, { color: tint(ctx, 'wallCream'), name: 'body' })
  g.add(inked(body))
  g.add(boxOn(t * 0.86, t * 0.1, t * 0.66, { color: 'floorTile', y: t * 0.68 }))
  g.add(box(t * 0.5, t * 0.1, t * 0.4, { color: 'metal', y: t * 0.76 }))
  g.add(cylinder(t * 0.045, t * 0.34, { color: 'metal', y: t * 0.78, z: -t * 0.24 }))
  g.add(box(t * 0.045, t * 0.045, t * 0.22, { color: 'metal', y: t * 1.11, z: -t * 0.14, name: 'tap' }))
  const water = group('water')
  water.visible = flag(ctx, 'running', false)
  water.add(box(t * 0.07, t * 0.32, t * 0.07, { color: 'water', y: t * 0.92, z: -t * 0.05 }))
  water.add(cylinder(t * 0.16, t * 0.04, { color: 'water', y: t * 0.76 }))
  g.add(water)
  grounded(g, ctx, t * 0.98, t * 0.9)
  return g
}

export function artBath(ctx: ArtContext): THREE.Object3D {
  const t = ctx.grid.tileSize
  const g = group('bath')
  const c = tint(ctx, 'uiPanel')
  // Built as four rims around a floor, NOT a solid box with a lid: a kid has to
  // be able to see that there is an inside to fill.
  const W = t * 1.7
  const D = t * 1.0
  const H = t * 0.62
  const R = t * 0.13
  const base = boxOn(W, t * 0.14, D, { color: c, y: t * 0.16, name: 'shell' })
  g.add(inked(base))
  g.add(boxOn(W, H, R, { color: c, y: t * 0.16, z: (D - R) / 2 }))
  g.add(boxOn(W, H, R, { color: c, y: t * 0.16, z: -(D - R) / 2 }))
  g.add(boxOn(R, H, D - R * 2, { color: c, x: (W - R) / 2, y: t * 0.16 }))
  g.add(boxOn(R, H, D - R * 2, { color: c, x: -(W - R) / 2, y: t * 0.16 }))
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      g.add(cylinder(t * 0.08, t * 0.16, { color: 'metalDark', x: sx * t * 0.7, z: sz * t * 0.34, seg: 8 }))
    }
  }
  g.add(cylinder(t * 0.05, t * 0.3, { color: 'metal', x: -t * 0.74, y: t * 0.78 }))
  g.add(box(t * 0.18, t * 0.05, t * 0.05, { color: 'metal', x: -t * 0.65, y: t * 1.06, name: 'tap' }))

  const water = group('water')
  water.visible = flag(ctx, 'full', false)
  const surface = boxOn(W - R * 2.2, t * 0.34, D - R * 2.2, { color: 'water', y: t * 0.3 })
  surface.material = toon('water')
  water.add(surface)
  for (const [dx, dz, r] of [
    [-0.4, 0.1, 0.13],
    [0.15, -0.16, 0.16],
    [0.5, 0.14, 0.11],
  ] as const) {
    water.add(ball(t * r, { color: 'uiPanel', x: dx * t, y: t * 0.66, z: dz * t, seg: 8 }))
  }
  g.add(water)
  grounded(g, ctx, t * 1.92, t * 0.96)
  return g
}

// ---------------------------------------------------------------------------
// lights, decor, toys
// ---------------------------------------------------------------------------

/**
 * A LAMP IS A SHADE ON A POLE, AND THE POLE HAS TO BE VISIBLE.
 *
 * ROUND-2 FIX, two faults in one head:
 *
 *  - The lit group carried a flat `sunWarm` DISC 0.52 of a tile across, floating
 *    just under the shade. At an isometric angle a bright horizontal disc in
 *    mid-air is not a glow, it is a table top — the critic saw "the yellow stool
 *    floating above the white side table" and "a hard-edged elliptical bright
 *    patch rather than a light". The disc is gone. The halo ball (soft, additive,
 *    round) and the pool on the floor do the job the disc was meant to do.
 *  - The stems were 0.045 and 0.05 of a tile — under two device pixels once the
 *    render scale is applied, so they vanished and the shade appeared to hover.
 *    Both are thicker now and ink-outlined, which is what makes a thin thing
 *    survive at this resolution.
 */
function lampHead(g: THREE.Group, t: number, y: number, on: boolean, shade: PaletteName): void {
  // `bulb` / `bulbOff` are GROUPS so one toggle swaps the bulb and its glow
  // together — see the naming contract at the top of this file.
  const lit = group('bulb')
  const bulb = ball(t * 0.13, { color: 'sunWarm', y: y - t * 0.02, seg: 8 })
  bulb.material = toon('sunWarm')
  bulb.castShadow = false
  lit.add(bulb)
  // A lamp that emits NOTHING is just a hat on a stick. The halo sells the
  // bulb, the floor pool sells the room being lit by it, and both switch with
  // the same `bulb` group a kid toggles by tapping.
  const halo = ball(t * 0.3, { color: 'sunWarm', y: y - t * 0.02, seg: 10 })
  halo.material = haloMaterial()
  halo.renderOrder = 2
  halo.castShadow = false
  lit.add(halo)
  lit.add(lightPool(t, t * 0.68))
  lit.visible = on
  const dark = group('bulbOff')
  dark.add(ball(t * 0.11, { color: 'uiDisabled', y: y - t * 0.02, seg: 8 }))
  dark.visible = !on
  g.add(lit, dark)

  const sh = cone(t * 0.3, t * 0.34, { color: shade, seg: 12 })
  sh.name = 'shade'
  sh.rotation.x = Math.PI
  sh.position.y = y + t * 0.34
  g.add(inked(sh))
}

export function artTableLamp(ctx: ArtContext): THREE.Object3D {
  const t = ctx.grid.tileSize
  const g = group('lamp')
  const on = flag(ctx, 'on', true)
  const base = cylinder(t * 0.19, t * 0.1, { color: 'woodDark', name: 'base' })
  g.add(inked(base))
  // Thicker, and DARK against a cream wall. Not ink-outlined: `inked` doubles a
  // draw call and a lamp stem is the wrong place to spend one — the width is
  // what makes it survive the render scale, not the outline.
  g.add(cylinder(t * 0.085, t * 0.4, { color: 'woodDark', y: t * 0.1 }))
  lampHead(g, t, t * 0.5, on, tint(ctx, 'flowerYellow'))
  grounded(g, ctx, t * 0.56, t * 0.56)
  return g
}

export function artFloorLamp(ctx: ArtContext): THREE.Object3D {
  const t = ctx.grid.tileSize
  const g = group('floorLamp')
  const on = flag(ctx, 'on', true)
  const base = cylinder(t * 0.28, t * 0.11, { color: 'woodDark', name: 'base' })
  g.add(inked(base))
  g.add(cylinder(t * 0.075, t * 1.23, { color: 'woodDark', y: t * 0.11 }))
  lampHead(g, t, t * 1.32, on, tint(ctx, 'flowerPink'))
  grounded(g, ctx, t * 0.68, t * 0.68)
  return g
}

export function artRug(ctx: ArtContext): THREE.Object3D {
  const t = ctx.grid.tileSize
  const g = group('rug')
  const c = tint(ctx, 'floorRug')
  const pad = group('pad')
  pad.add(cylinder(t * 0.94, t * 0.04, { color: c, seg: 16, name: 'base' }))
  pad.add(cylinder(t * 0.68, t * 0.045, { color: 'wallCream', seg: 16 }))
  pad.add(cylinder(t * 0.4, t * 0.05, { color: c, seg: 16 }))
  pad.add(cylinder(t * 0.16, t * 0.055, { color: 'uiCoin', seg: 12 }))
  g.add(pad)
  return g
}

export function artPlant(ctx: ArtContext): THREE.Object3D {
  const t = ctx.grid.tileSize
  const g = group('plant')
  /*
   * THE POT SITS ON THE FLOOR. `cone()` already centres the geometry at half its
   * height, so overwriting `position.y` with the FULL height parked the pot
   * 0.17 of a tile in the air — "the plant next to the TV sits slightly proud of
   * the floor", and it was proud of it in every room the plant appears in.
   */
  const pot = cone(t * 0.28, t * 0.34, { color: tint(ctx, 'roofRed'), name: 'pot', seg: 10 })
  pot.rotation.x = Math.PI
  pot.position.y = t * 0.17
  g.add(inked(pot))
  g.add(cylinder(t * 0.23, t * 0.05, { color: 'dirtDark', y: t * 0.28 }))
  const leaves = group('leaves')
  leaves.position.y = t * 0.32
  const shades: PaletteName[] = ['leaf', 'leafLight', 'leafDark']
  for (let i = 0; i < 5; i++) {
    const a = (i / 5) * Math.PI * 2 + ctx.variety * 2
    const blade = boxOn(t * 0.1, t * 0.5 + (i % 2) * t * 0.16, t * 0.24, {
      color: shades[i % 3] ?? 'leaf',
      x: Math.cos(a) * t * 0.16,
      z: Math.sin(a) * t * 0.16,
      rotY: -a,
    })
    blade.rotation.z = Math.cos(a) * 0.3
    blade.rotation.x = Math.sin(a) * 0.3
    leaves.add(blade)
  }
  g.add(leaves)
  grounded(g, ctx, t * 0.68, t * 0.68)
  return g
}
